<template>
    <div class="stream-menu-wrapper">
        <button class="icon-btn" type="button" aria-label="Stream" :aria-expanded="visible" @click="$emit('toggle')">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none"> <path d="M7 12L17 12" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/> <path d="M7 8L13 8" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/> <path d="M3 20.2895V5C3 3.89543 3.89543 3 5 3H19C20.1046 3 21 3.89543 21 5V15C21 16.1046 20.1046 17 19 17H7.96125C7.35368 17 6.77906 17.2762 6.39951 17.7506L4.06852 20.6643C3.71421 21.1072 3 20.8567 3 20.2895Z" stroke="currentColor" stroke-width="1.5"/> </svg>
        </button>

        <transition name="stream-menu">
            <div class="stream-menu" v-if="visible" @click.stop>
                <div class="stream-menu-columns" :class="{ 'is-single': !hasAudio || !hasSubtitles }">

                    <div class="stream-menu-column" v-if="hasAudio">
                        <div class="stream-menu-header">Audio</div>
                        <div class="stream-menu-list">
                            <button
                                v-for="(a, i) in audioTracks"
                                :key="'audio-' + i"
                                type="button"
                                class="stream-menu-item"
                                :class="{ 'is-active': a.index === activeAudioIndex }"
                                @click="$emit('change-audio', a.index)"
                            >
                                <svg class="check-icon" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                                    <path d="M3.5 8.5L6.2 11.2L12.5 4.5" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/>
                                </svg>
                                <span class="item-label">{{ a.title || langName(a.language) }}</span>
                            </button>
                        </div>
                    </div>

                    <div class="stream-menu-column" v-if="hasSubtitles">
                        <div class="stream-menu-header">Subtitles</div>
                        <div class="stream-menu-list">
                            <button
                                type="button"
                                class="stream-menu-item"
                                :class="{ 'is-active': activeSubtitleIndex === null }"
                                @click="$emit('change-subtitle', null)"
                            >
                                <svg class="check-icon" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                                    <path d="M3.5 8.5L6.2 11.2L12.5 4.5" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/>
                                </svg>
                                <span class="item-label">Off</span>
                            </button>

                            <template v-for="(s, i) in subtitles">
                                <div
                                    v-if="i === subtitleDividerIndex"
                                    :key="'divider-' + i"
                                    class="stream-menu-divider"
                                ></div>
                                <button
                                    :key="'sub-' + i"
                                    type="button"
                                    class="stream-menu-item"
                                    :class="{ 'is-active': s.index === activeSubtitleIndex }"
                                    @click="$emit('change-subtitle', s.index)"
                                >
                                    <svg class="check-icon" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                                        <path d="M3.5 8.5L6.2 11.2L12.5 4.5" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/>
                                    </svg>
                                    <span class="item-label">{{ s.title || langName(s.language) }}</span>
                                </button>
                            </template>
                        </div>
                    </div>

                </div>
            </div>
        </transition>
    </div>
</template>

<script>

//schemas
 //"audioTracks": [
 //{
   //  "index": 1,
     //"language": "eng",
     //"title": null || text,
     //"codec": "eac3",
     //"channels": 6,
    // "default": true
 //}
   // ],
 //"subtitles": [
 //{
     //"index": 2,
   //  "language": "eng",
     //"title": null || text,
     //"codec": "ass",
     //"default": true
 //}
  //  ]

// Common ISO 639-2/639-1 codes -> display names, used only as a fallback
// when a track has no "title" from the source.
const LANGUAGE_NAMES = {
    eng: 'English', en: 'English',
    spa: 'Spanish', es: 'Spanish',
    fre: 'French', fra: 'French', fr: 'French',
    ger: 'German', deu: 'German', de: 'German',
    ita: 'Italian', it: 'Italian',
    por: 'Portuguese', pt: 'Portuguese',
    rus: 'Russian', ru: 'Russian',
    jpn: 'Japanese', ja: 'Japanese',
    kor: 'Korean', ko: 'Korean',
    chi: 'Chinese', zho: 'Chinese', zh: 'Chinese',
    ara: 'Arabic', ar: 'Arabic',
    dan: 'Danish', da: 'Danish',
    dut: 'Dutch', nld: 'Dutch', nl: 'Dutch',
    swe: 'Swedish', sv: 'Swedish',
    nor: 'Norwegian', no: 'Norwegian',
    fin: 'Finnish', fi: 'Finnish',
    pol: 'Polish', pl: 'Polish',
    tur: 'Turkish', tr: 'Turkish',
    heb: 'Hebrew', he: 'Hebrew',
    hin: 'Hindi', hi: 'Hindi',
    tha: 'Thai', th: 'Thai',
    vie: 'Vietnamese', vi: 'Vietnamese',
    cze: 'Czech', ces: 'Czech', cs: 'Czech',
    gre: 'Greek', ell: 'Greek', el: 'Greek',
    hun: 'Hungarian', hu: 'Hungarian',
    rum: 'Romanian', ron: 'Romanian', ro: 'Romanian',
    ukr: 'Ukrainian', uk: 'Ukrainian',
    ind: 'Indonesian', id: 'Indonesian',
    may: 'Malay', msa: 'Malay', ms: 'Malay',
}

export default {
    name: 'StreamMenu',
    props: {
        visible: { type: Boolean, default: false },

        audioTracks: { type: Array, default: () => [] },
        subtitles: { type: Array, default: () => [] },

        activeAudioIndex: { type: Number, default: null },
        activeSubtitleIndex: { type: Number, default: null },

        // Optional: insert a divider in the subtitles list after this
        // zero-based index (e.g. to separate "top" languages from the
        // rest, as in the reference design). Leave unset for no divider.
        subtitleDividerIndex: { type: Number, default: null },
    },
    computed: {
        hasAudio() {
            return Array.isArray(this.audioTracks) && this.audioTracks.length > 0
        },
        hasSubtitles() {
            return Array.isArray(this.subtitles) && this.subtitles.length > 0
        },
    },
    methods: {
        langName(code) {
            if (!code) return 'Unknown'
            const key = String(code).toLowerCase()
            if (LANGUAGE_NAMES[key]) return LANGUAGE_NAMES[key]
            return key.charAt(0).toUpperCase() + key.slice(1)
        },
    },
}
</script>

<style lang="scss" scoped>
.stream-menu-wrapper {
    position: relative;
    display: inline-flex;
}

.stream-menu {
    position: absolute;
    bottom: calc(100% + 32px);
    left: -380px;
    z-index: 20;
    width: 400px;
    background: rgba(24, 24, 26, 0.96);
    backdrop-filter: blur(20px) saturate(1.3);
    -webkit-backdrop-filter: blur(20px) saturate(1.3);
    border: 1px solid rgba(255, 255, 255, 0.06);
    border-radius: 10px;
    box-shadow: 0 16px 40px rgba(0, 0, 0, 0.55), 0 2px 10px rgba(0, 0, 0, 0.35);
    padding: 24px 28px;
    transform-origin: bottom center;
}

.stream-menu-columns {
    display: flex;
    gap: 56px;

    &.is-single {
        .stream-menu-column {
            min-width: 200px;
        }
    }
}

.stream-menu-column {
    display: flex;
    flex-direction: column;
    min-width: 160px;

    &:last-child {
        min-width: 220px;
    }
}

.stream-menu-header {
    font-size: 19px;
    font-weight: 800;
    color: #fff;
    margin: 0 0 14px;
}

.stream-menu-divider {
    height: 1px;
    margin: 6px 0 10px;
    background: rgba(255, 255, 255, 0.16);
}

.stream-menu-list {
    display: flex;
    flex-direction: column;
    max-height: 340px;
    overflow-y: auto;
    mask-image: linear-gradient(to bottom, #000 calc(100% - 28px), transparent 100%);
    -webkit-mask-image: linear-gradient(to bottom, #000 calc(100% - 28px), transparent 100%);

    &::-webkit-scrollbar {
        width: 0;
    }
}

span{
    text-overflow: ellipsis;
    width: calc(100% - 50px);
    overflow: hidden;
}
.stream-menu-item {
    display: flex;
    align-items: center;
    overflow:hidden;
    gap: 10px;
    width: 100%;
    background: none;
    border: none;
    padding: 12px 0;
    color: rgba(255, 255, 255, 0.5);
    font-size: 15px;
    font-weight: 500;
    text-align: left;
    cursor: pointer;
    transition: color 0.15s ease;
    white-space: nowrap;

    &:hover {
        color: rgba(255, 255, 255, 0.85);
    }

    &.is-active {
        color: #fff;
        font-weight: 700;
    }

    .check-icon {
        flex-shrink: 0;
        width: 15px;
        height: 15px;
        color: #fff;
        opacity: 0;
    }

    &.is-active .check-icon {
        opacity: 1;
    }
}
</style>
